import { FilterFixed, useFilterDataHook } from '@op/components/FilterCommon';
import React, { useMemo, useRef } from 'react';
import './index.scss';
import { TKeywordUrlPreviewFilterProps } from '../../../types';
import { FilterKeys, KeyOperateType } from '@op/components/FilterCommon/types';
import { asyncCreateShortUrl } from '@op/components/FilterCommon/utils/historyUtils';
import { PromptModalType } from '@op/components/GPT/hooks/usePromptModal'; 
import useAdvancedSummary from '@op/components/GPT/hooks/useAdvancedSummary'; 
import {
  keywordDrillUrlPreviewsDisabledTips,
  TipsMapType,
} from '@op/components/BubbleCommon';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { getShortUrlParse } from '@op/services';
import { useSummaryUpdateLog } from '../../../hook';
import useLanguage from '@op/hooks/useLanguage';

const resetTips: TipsMapType = {
  zh: '重置为报告初始筛选条件及AI报告',
  en: 'Reset to original filters and AI report',
};

const resetDisabledTips: TipsMapType = {
  zh: '当前已是初始报告',
  en: 'This is already the original report',
};

export default function KeywordUrlPreviewFilter({
  gameInfo,
  changeFilterValue,
  shortId: pramsShortId,
  setLoading,
  tab,
  wholeGameInfo,
  promptList,
  videoPromptList, 
  leadId,
  updateUrlSummary,
  loading,
  isCreator,
  resetAnalysis,
  setTranslateTopicMap,
  hideFilterOperateBtn,
  allPreview,
}: TKeywordUrlPreviewFilterProps) {
  const { formatMessage } = useLanguage();
  const isFirst = useRef(true);
  const {
    remainder,
    total,
    loading: advancedLimitLoading,
  } = useAdvancedSummary({ defaultLoading: true });

  function getPromptRealDetail(item?: PromptModalType) {
    const { advancedPrompt, prompt, gptOptions, ...data } = item || {};
    return {
      ...data,
      gptOptions,
      prompt: gptOptions === 'basic' ? prompt : advancedPrompt,
    };
  }

  function handleErrorConfirm() {
    changeFilterValue({
      filter: null,
      oriFilter: null,
      pageFilter: null,
      tab,
    });
  }

  const dynamicsConfig = useMemo(() => {
    return {
      remainder,
      total,
    };
  }, [remainder, total]);

  const { scrollBody, filterOptions, confirmRef, filterConfirm } =
    useFilterDataHook({
      gameInfo,
      pageName: 'Summary_Keywords_Drill',
    });

  const filterOptionsList: Array<FilterKeys> = useMemo(() => {
    const list: Array<FilterKeys> = filterOptions.filter(
      (item) => item !== 'monitor' && item !== 'postAuthorType',
    );
    return [...list, 'keywordPreview', 'gptPreview', 'urlPreview'];
  }, [filterOptions]);

  function getKeyOperateShowOptions(): KeyOperateType[] {
    // 只读内嵌场景：隐藏全部操作按钮（确定 / 重置 / Stop）
    if (hideFilterOperateBtn) return [];
    return isCreator ? ['confirm', 'reset'] : ['confirm'];
  }

  async function urlConfirm(filter: any, saveFilter: any) {
    // URL的情况下去掉官号筛选器
    const {
      social_media_by_account,
      social_media_by_keyword,
      promptInfo,
      gptList,
      ...otherFilter
    } = filter;
    const {
      social_media_by_account: a,
      social_media_by_keyword: b,
      ...otherSaveFilter
    } = saveFilter;
    let task_data: any = {};
    const urlSummaryTask: any = { lead_id: leadId, short_url: pramsShortId };
    try {
      if (!isFirst.current) {
        const short_url = await asyncCreateShortUrl({
          filter: otherSaveFilter,
          gameInfo,
          updateUrl: false,
          errorText: formatMessage({
            id: 'Analysis Generate failed, please try again',
          }),
        });

        if (!short_url) return;

        urlSummaryTask.short_url = short_url;

        const keyFilterMap: any = {
          comments: {
            key: 'comments_task',
            paramsKey: 'comment_task_id',
            filter: { scenario: 'url_comments_task' },
            errorKey: 'comment_error_code',
          },
          video: {
            key: 'video_task',
            paramsKey: 'video_task_id',
            filter: { scenario: 'url_video_task' },
            errorKey: 'video_error_code',
          },
        };

        for (const key of gptList) {
          const element = promptInfo[key];
          const data = getPromptRealDetail(element);
          const {
            key: realKey,
            filter,
            paramsKey,
            errorKey,
          } = keyFilterMap[key];
          try {
            // const id = await advancedConfirm(
            //   element,
            //   { ...otherFilter, ...filter },
            //   false,
            // );
            // 不做自动请求GPT
            task_data[realKey] = { error_code: '80000', task_id: '' };
            urlSummaryTask[paramsKey] = '-1';
          } catch (error: any) {
            // if (error.code === 30018) {
            //   handleErrorConfirm();
            //   return;
            // }
            urlSummaryTask[errorKey] = error.code;
            task_data[realKey] = { error_code: error.code, task_id: '' };
          }
        }
        await updateUrlSummary?.(urlSummaryTask);
      } else {
        task_data = null;
      }

      changeFilterValue({
        filter: otherFilter,
        oriFilter: otherSaveFilter,
        tab,
        pageFilter: {
          task_id: String(leadId),
          short_url: urlSummaryTask.short_url,
          category: tab,
          ...gameInfo,
        },
        updateFilter: {
          id: leadId,
          short_url: urlSummaryTask.short_url,
          category: tab,
          ...gameInfo,
        },
        promptInfo,
        gptList,
        urlTaskId: leadId,
        taskData: task_data,
        isFirst: isFirst.current,
      });
    } catch (error) {
      handleErrorConfirm();
    }

    isFirst.current = false;
  }

  function dealwithOperationLog(data: any) {
    const { promptInfo, ...filter } = data || {};
    const keys = Object.keys(promptInfo || {});

    const dataInfo = keys.reduce((pre, cur) => {
      const { prompt, ...info } = promptInfo[cur];
      return { ...pre, [cur]: info };
    }, {});

    return {
      ...filter,
      promptInfo: dataInfo || {},
    };
  }

  async function restUrlConfirm(filter: any, saveFilter: any, report?: any) {
    if (!report) return;
    // URL的情况下去掉官号筛选器
    const {
      social_media_by_account,
      social_media_by_keyword,
      promptInfo,
      gptList,
      ...otherFilter
    } = filter;
    const {
      social_media_by_account: a,
      social_media_by_keyword: b,
      ...otherSaveFilter
    } = saveFilter;
    try {
      changeFilterValue({
        filter: otherFilter,
        oriFilter: otherSaveFilter,
        tab,
        pageFilter: {
          task_id: String(leadId),
          short_url: report.short_url,
          category: tab,
          ...gameInfo,
        },
        updateFilter: {
          id: leadId,
          short_url: report.short_url,
          category: tab,
          ...gameInfo,
        },
        promptInfo,
        gptList,
        urlTaskId: leadId,
      });
    } catch (error) {
      handleErrorConfirm();
    }
  }

  const {
    loading: logLoading,
    resetDisabled,
    getSummaryLog,
    setLoading: setLogLoading,
  } = useSummaryUpdateLog({
    gameInfo,
    tab,
  });

  const reportData = useRef(null);
  async function confirm(filterData: any, oriFilterValue: any) {
    setLoading(true);
    const filter = { ...filterData, ...gameInfo };
    const saveFilter = { ...oriFilterValue, ...gameInfo };
    const confirmFunc: (
      filterData: any,
      oriFilterValue: any,
      reportData?: any,
    ) => Promise<void> = reportData.current ? restUrlConfirm : urlConfirm;

    try {
      await confirmFunc(filter, saveFilter, reportData.current);
      getSummaryLog();
      const operationFilter = dealwithOperationLog(filter);

      buriedBtnPoint(
        'GenerateReport',
        operationFilter,
        saveFilter?.keywordCustomInput,
        `${saveFilter?.content_urls}`,
      );
    } catch (error) {}
    reportData.current = null;
    setLoading(false);
  }

  return (
    <div className="keyword-url-filter-wrapper">
      {!advancedLimitLoading && (
        <FilterFixed
          filter={filterOptionsList}
          onlySaveFilters={['keyWords', 'urlAnalysis']}
          confirmRef={confirmRef}
          operateShowOptions={['more']}
          isPreviews={hideFilterOperateBtn}
          allPreview={allPreview}
          keyOperateShowOptions={getKeyOperateShowOptions()}
          scrollBody={scrollBody}
          shortid={pramsShortId}
          beforeCacheDealwithData={() => false}
          defaultConfig={{
            gptPreview: {
              wholeGameInfo,
              tab,
              promptList,
              videoPromptList,
            },
          }}
          finallyDealwithDefaultValue={(
            filter,
            currentFilter,
            isDefaultValue,
          ) => {
            if (isDefaultValue) {
              return currentFilter;
            }
            return filter;
          }}
          finallyDealwithResetValue={async () => {
            setLogLoading(true);
            const reportInfo = await resetAnalysis?.();
            if (reportInfo) {
              const info = await getShortUrlParse(reportInfo.short_url);
              reportData.current = reportInfo;

              setTimeout(() => {
                filterConfirm?.(true);
              }, 1500);

              if (info) {
                return info;
              }
            }
          }}
          disabledTips={
            loading ? keywordDrillUrlPreviewsDisabledTips : undefined
          }
          dynamicsConfig={dynamicsConfig}
          confirm={(value: any, oriFilterValue: any) => {
            confirm(value, oriFilterValue);
          }}
          resetBtnText={'Revert to original report'}
          resetBtnTips={resetDisabled ? resetDisabledTips : resetTips}
          resetDisabled={resetDisabled}
          resetLoading={logLoading}
          backCondOriginData={(data) => {
            setTranslateTopicMap?.(data?.topic_trans || []);
          }}
        ></FilterFixed>
      )}
    </div>
  );
}
